import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { CheckCircle, XCircle, Clock, User, Briefcase, MessageSquare } from 'lucide-react';
import styles from './DepartmentApplicationsTab.module.css';

interface Position {
  id: number;
  title: string;
  department: string;
}

interface ApplicationUser {
  id: number;
  username: string;
  email: string;
}

interface Application {
  id: number;
  userId: number;
  positionId: number;
  applicationMessage: string | null;
  status: number; // 1=Pending, 2=Approved, 3=Denied, 4=Withdrawn
  responseMessage: string | null;
  reviewedByUserId: number | null;
  createdAt: string;
  updatedAt: string | null;
  user: ApplicationUser;
  position: Position;
  reviewedBy: ApplicationUser | null;
}

interface DepartmentApplicationsTabProps {
  department: string;
}

const getStatusLabel = (status: number) => {
  switch (status) {
    case 1: return 'Pending';
    case 2: return 'Approved';
    case 3: return 'Denied';
    case 4: return 'Withdrawn';
    default: return 'Unknown';
  }
};

const getStatusColor = (status: number) => {
  switch (status) {
    case 1: return '#f59e0b'; // Pending - orange
    case 2: return '#10b981'; // Approved - green
    case 3: return '#ef4444'; // Denied - red
    case 4: return '#6b7280'; // Withdrawn - gray
    default: return '#6b7280';
  }
};

const DepartmentApplicationsTab: React.FC<DepartmentApplicationsTabProps> = ({ department }) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'denied'>('pending');
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [filter, department]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/PositionApplications`);
      
      // Filter by department
      const filtered = response.data.filter((app: Application) => 
        app.position.department.toLowerCase() === department.toLowerCase()
      );

      // Apply status filter
      const statusFiltered = filter === 'all' 
        ? filtered 
        : filtered.filter((app: Application) => {
            if (filter === 'pending') return app.status === 1;
            if (filter === 'approved') return app.status === 2;
            if (filter === 'denied') return app.status === 3;
            return true;
          });

      setApplications(statusFiltered);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (appId: number) => {
    if (!responseMessage.trim()) {
      alert('Please provide a response message.');
      return;
    }

    try {
      setProcessing(true);
      await axios.put(`/PositionApplications/${appId}/approve`, {
        responseMessage: responseMessage.trim()
      });
      alert('Application approved successfully!');
      setSelectedApp(null);
      setResponseMessage('');
      fetchApplications();
    } catch (error: any) {
      console.error('Failed to approve application:', error);
      alert(error.response?.data || 'Failed to approve application.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeny = async (appId: number) => {
    if (!responseMessage.trim()) {
      alert('Please provide a reason for denial.');
      return;
    }

    try {
      setProcessing(true);
      await axios.put(`/PositionApplications/${appId}/deny`, {
        responseMessage: responseMessage.trim()
      });
      alert('Application denied.');
      setSelectedApp(null);
      setResponseMessage('');
      fetchApplications();
    } catch (error: any) {
      console.error('Failed to deny application:', error);
      alert(error.response?.data || 'Failed to deny application.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading applications...</div>;
  }

  return (
    <div className={styles.container}>
      {/* Filter Tabs */}
      <div className={styles.filterTabs}>
        <button
          className={`${styles.filterTab} ${filter === 'pending' ? styles.active : ''}`}
          onClick={() => setFilter('pending')}
        >
          <Clock size={16} />
          Pending
        </button>
        <button
          className={`${styles.filterTab} ${filter === 'approved' ? styles.active : ''}`}
          onClick={() => setFilter('approved')}
        >
          <CheckCircle size={16} />
          Approved
        </button>
        <button
          className={`${styles.filterTab} ${filter === 'denied' ? styles.active : ''}`}
          onClick={() => setFilter('denied')}
        >
          <XCircle size={16} />
          Denied
        </button>
        <button
          className={`${styles.filterTab} ${filter === 'all' ? styles.active : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
      </div>

      {/* Applications List */}
      {applications.length === 0 ? (
        <div className={styles.emptyState}>
          <Briefcase size={48} />
          <p>No {filter !== 'all' ? filter : ''} applications found for this department.</p>
        </div>
      ) : (
        <div className={styles.applicationsList}>
          {applications.map((app) => (
            <div key={app.id} className={styles.applicationCard}>
              <div className={styles.cardHeader}>
                <div className={styles.userInfo}>
                  <User size={20} />
                  <div>
                    <h3>{app.user.username}</h3>
                    <p className={styles.email}>{app.user.email}</p>
                  </div>
                </div>
                <span
                  className={styles.statusBadge}
                  style={{ backgroundColor: getStatusColor(app.status) }}
                >
                  {getStatusLabel(app.status)}
                </span>
              </div>

              <div className={styles.positionInfo}>
                <Briefcase size={18} />
                <div>
                  <h4>{app.position.title}</h4>
                  <p>{app.position.department}</p>
                </div>
              </div>

              {app.applicationMessage && (
                <div className={styles.messageSection}>
                  <MessageSquare size={16} />
                  <p className={styles.applicationMessage}>{app.applicationMessage}</p>
                </div>
              )}

              {app.responseMessage && (
                <div className={styles.responseSection}>
                  <p><strong>Response:</strong> {app.responseMessage}</p>
                  {app.reviewedBy && (
                    <p className={styles.reviewedBy}>Reviewed by: {app.reviewedBy.username}</p>
                  )}
                </div>
              )}

              <div className={styles.cardFooter}>
                <span className={styles.date}>
                  Applied: {new Date(app.createdAt).toLocaleDateString()}
                </span>
                {app.status === 1 && (
                  <button
                    className={styles.reviewButton}
                    onClick={() => {
                      setSelectedApp(app);
                      setResponseMessage('');
                    }}
                  >
                    Review Application
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {selectedApp && (
        <div className={styles.modalOverlay} onClick={() => setSelectedApp(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Review Application</h2>
              <button className={styles.closeButton} onClick={() => setSelectedApp(null)}>
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.reviewInfo}>
                <p><strong>Applicant:</strong> {selectedApp.user.username}</p>
                <p><strong>Position:</strong> {selectedApp.position.title}</p>
                <p><strong>Department:</strong> {selectedApp.position.department}</p>
                {selectedApp.applicationMessage && (
                  <div className={styles.messageBox}>
                    <strong>Application Message:</strong>
                    <p>{selectedApp.applicationMessage}</p>
                  </div>
                )}
              </div>

              <div className={styles.responseInput}>
                <label htmlFor="response">Your Response:</label>
                <textarea
                  id="response"
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Provide feedback or welcome message..."
                  rows={4}
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.approveButton}
                onClick={() => handleApprove(selectedApp.id)}
                disabled={processing || !responseMessage.trim()}
              >
                <CheckCircle size={18} />
                Approve & Assign
              </button>
              <button
                className={styles.denyButton}
                onClick={() => handleDeny(selectedApp.id)}
                disabled={processing || !responseMessage.trim()}
              >
                <XCircle size={18} />
                Deny
              </button>
              <button
                className={styles.cancelButton}
                onClick={() => setSelectedApp(null)}
                disabled={processing}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentApplicationsTab;
